import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Send,
  Bot,
  User,
  Loader2,
  RotateCcw,
  Copy,
  CheckCircle,
  MessageSquare,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";
const N8N_BASE = "https://n8n.hyveappliedintelligence.com";

const RAGChatbot = () => {
  const { user, token } = useAuth();
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState("");
  const [facilityConfig, setFacilityConfig] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [copiedIdx, setCopiedIdx] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const scrollToBottom = () =>
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch facilities that have RAG service
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const res = await fetch(`${AUTH_URL}/facilities`, { headers });
        if (res.ok) {
          const data = await res.json();
          const list = (Array.isArray(data) ? data : []).filter(
            (f) =>
              f.pinecone_namespace &&
              f.primary_product_code &&
              (f.primary_product_code.includes("RAG") ||
                f.primary_product_code === "RAG_SLM" ||
                f.primary_product_code === "PRIVATE_LLM_RAG"),
          );
          setFacilities(list);
        }
      } catch (err) {
        console.error("Failed to load facilities:", err);
      }
    };
    fetchFacilities();
  }, [token]);

  // When facility changes, reset chat and load config
  useEffect(() => {
    if (!facilityId) {
      setFacilityConfig(null);
      return;
    }
    const fac = facilities.find((f) => f.facility_id === facilityId);
    setFacilityConfig(fac || null);
    setSessionId(`rag-${facilityId}-${user?.email || "anon"}-${Date.now()}`);
    setMessages([
      {
        role: "assistant",
        content: `Connected to ${fac?.facility_name || facilityId} knowledge base. Ask me anything about your documents.`,
        timestamp: new Date(),
      },
    ]);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [facilityId]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || sending || !facilityId) return;

    setMessages((prev) => [
      ...prev,
      { role: "user", content: text, timestamp: new Date() },
    ]);
    setInput("");
    setSending(true);

    // Try facility-specific webhook first, fall back to generic
    const webhookUrl = `${N8N_BASE}/webhook/rag-chat/${facilityId}`;

    try {
      const res = await fetch(webhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chatInput: text,
          sessionId,
          action: "sendMessage",
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

      const data = await res.json();
      const reply =
        data.output || data.text || data.response || JSON.stringify(data);

      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: reply, timestamp: new Date() },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Error: ${err.message}. The RAG chatbot for ${facilityId} may not be provisioned yet.`,
          timestamp: new Date(),
          isError: true,
        },
      ]);
    }
    setSending(false);
  }, [input, sending, facilityId, sessionId]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleCopy = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const resetChat = () => {
    setSessionId(`rag-${facilityId}-${user?.email || "anon"}-${Date.now()}`);
    setMessages([
      {
        role: "assistant",
        content: "Session reset. How can I help you?",
        timestamp: new Date(),
      },
    ]);
  };

  const formatContent = (text) => {
    return text.split("\n").map((line, i) => {
      if (!line.trim()) return <br key={i} />;
      const parseInline = (str) => {
        const parts = [];
        let remaining = str;
        let key = 0;
        while (remaining.length > 0) {
          const boldMatch = remaining.match(/\*\*(.*?)\*\*/);
          const codeMatch = remaining.match(/`([^`]+)`/);
          const matches = [
            boldMatch && {
              type: "bold",
              index: boldMatch.index,
              length: boldMatch[0].length,
              content: boldMatch[1],
            },
            codeMatch && {
              type: "code",
              index: codeMatch.index,
              length: codeMatch[0].length,
              content: codeMatch[1],
            },
          ]
            .filter(Boolean)
            .sort((a, b) => a.index - b.index);
          if (matches.length === 0) {
            parts.push(remaining);
            break;
          }
          const match = matches[0];
          if (match.index > 0) parts.push(remaining.slice(0, match.index));
          if (match.type === "bold")
            parts.push(<strong key={key++}>{match.content}</strong>);
          else
            parts.push(
              <code
                key={key++}
                className="px-1 py-0.5 bg-slate-100 rounded text-xs font-mono"
              >
                {match.content}
              </code>,
            );
          remaining = remaining.slice(match.index + match.length);
        }
        return parts;
      };
      if (line.startsWith("- ") || line.startsWith("* "))
        return (
          <li key={i} className="ml-4 list-disc">
            {parseInline(line.slice(2))}
          </li>
        );
      const numMatch = line.match(/^(\d+)\.\s/);
      if (numMatch)
        return (
          <li key={i} className="ml-4 list-decimal">
            {parseInline(line.slice(numMatch[0].length))}
          </li>
        );
      return <p key={i}>{parseInline(line)}</p>;
    });
  };

  return (
    <div className="space-y-4">
      {/* Facility selector */}
      <div className="flex items-center space-x-4">
        <MessageSquare className="w-5 h-5 text-blue-600" />
        <select
          value={facilityId}
          onChange={(e) => setFacilityId(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select a knowledge base...</option>
          {facilities.map((f) => (
            <option key={f.facility_id} value={f.facility_id}>
              {f.facility_name || f.facility_id}
            </option>
          ))}
        </select>
        {facilityConfig && (
          <span className="text-xs text-slate-400 font-mono">
            {facilityConfig.pinecone_index}/{facilityConfig.pinecone_namespace}
          </span>
        )}
      </div>

      {!facilityId ? (
        <div className="text-center py-16 text-slate-500">
          <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-sm">
            Select a facility with RAG enabled to start chatting.
          </p>
        </div>
      ) : (
        <div className="flex flex-col h-[calc(100vh-200px)]">
          {/* Chat header */}
          <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200 rounded-t-lg shadow-sm">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-800">
                  {facilityConfig?.facility_name || facilityId}
                </h2>
                <p className="text-xs text-slate-500">RAG Knowledge Base</p>
              </div>
            </div>
            <button
              onClick={resetChat}
              title="Reset conversation"
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto bg-slate-50 px-4 py-4 space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`flex space-x-2 max-w-[80%] ${msg.role === "user" ? "flex-row-reverse space-x-reverse" : ""}`}
                >
                  <div
                    className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center ${
                      msg.role === "user"
                        ? "bg-blue-600"
                        : msg.isError
                          ? "bg-red-500"
                          : "bg-slate-700"
                    }`}
                  >
                    {msg.role === "user" ? (
                      <User className="w-3.5 h-3.5 text-white" />
                    ) : (
                      <Bot className="w-3.5 h-3.5 text-white" />
                    )}
                  </div>
                  <div className="group relative">
                    <div
                      className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                        msg.role === "user"
                          ? "bg-blue-600 text-white rounded-br-md"
                          : msg.isError
                            ? "bg-red-50 text-red-700 border border-red-200 rounded-bl-md"
                            : "bg-white text-slate-700 shadow-sm border border-slate-200 rounded-bl-md"
                      }`}
                    >
                      <div className="space-y-1">
                        {formatContent(msg.content)}
                      </div>
                    </div>
                    {msg.role === "assistant" && !msg.isError && (
                      <button
                        onClick={() => handleCopy(msg.content, idx)}
                        className="absolute -bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-white border border-slate-200 rounded-md shadow-sm"
                      >
                        {copiedIdx === idx ? (
                          <CheckCircle className="w-3 h-3 text-green-500" />
                        ) : (
                          <Copy className="w-3 h-3 text-slate-400" />
                        )}
                      </button>
                    )}
                    <div
                      className={`text-[10px] text-slate-400 mt-1 ${msg.role === "user" ? "text-right" : ""}`}
                    >
                      {msg.timestamp.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="flex space-x-2">
                  <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center">
                    <Bot className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div className="px-4 py-3 bg-white rounded-2xl rounded-bl-md shadow-sm border border-slate-200">
                    <div className="flex space-x-1">
                      <div
                        className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0ms" }}
                      />
                      <div
                        className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"
                        style={{ animationDelay: "150ms" }}
                      />
                      <div
                        className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"
                        style={{ animationDelay: "300ms" }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3 bg-white border-t border-slate-200 rounded-b-lg">
            <div className="flex items-end space-x-3">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about your documents... (Enter to send)"
                rows={1}
                disabled={sending}
                className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 max-h-32 overflow-y-auto"
                style={{ minHeight: "42px" }}
              />
              <button
                onClick={sendMessage}
                disabled={sending || !input.trim()}
                className={`p-2.5 rounded-xl transition-colors ${
                  sending || !input.trim()
                    ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {sending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RAGChatbot;
